import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  NumberFormatter,
  Select,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBrandBootstrap,
  IconCalendar,
  IconClockExclamation,
  IconFileExport,
  IconFileTypography,
  IconSearch,
  IconUsersGroup,
} from "@tabler/icons-react";
import Avvvatars from "avvvatars-react";
import dayjs from "dayjs";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { repositoryPos } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { formatDateNotTimeZone } from "../../../common/FormatDate/FormatDate";
import { BASE_API_POS } from "../../../config";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getMasterOrgSelect,
  getTblInventoryTransactionTypeOutWard,
} from "../../../service/getSelectApi";

const dataGroupByOption = [
  { value: "dept", label: "Theo phòng ban" },
  { value: "emp", label: "Theo nhân viên" },
];

// Ngưỡng quá hạn, BE nhận theo phút (mặc định 1440 phút = 1 ngày)
const overdueOption = (minutes: number, label: string) => ({
  value: String(minutes),
  label: `Quá ${label}`,
});

const dataOverdueMinutesOption = [
  {
    group: "Phút",
    items: [15, 30, 45].map((m) => overdueOption(m, `${m} phút`)),
  },
  {
    group: "Giờ",
    items: [1, 2, 3, 4, 6, 8, 12, 18].map((h) =>
      overdueOption(h * 60, `${h} giờ`),
    ),
  },
  {
    group: "Ngày",
    items: [1, 2, 3, 4, 5, 6, 7, 10, 14, 21, 30, 45, 60, 90].map((d) =>
      overdueOption(d * 1440, `${d} ngày`),
    ),
  },
];

const hoursColumn = (
  accessorKey: string,
  header: string,
): MRT_ColumnDef<any> => ({
  accessorKey,
  header,
  size: 200,
  enableColumnActions: false,
  enableSorting: false,
  enableColumnFilter: false,
  mantineTableHeadCellProps: { align: "right" },
  mantineTableBodyCellProps: { align: "right" },
  Cell: ({ cell }) => (
    <NumberFormatter
      value={cell.getValue<number>() ?? 0}
      thousandSeparator="."
      decimalSeparator=","
      decimalScale={2}
    />
  ),
});

const OverduePendingOutboundReport = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const [visible, { open, close }] = useDisclosure(false);
  const [dataBranchSelect, setDataBranchSelect] = useState<ComboboxItem[]>([]);
  const [dataSourceTypeSelect, setDataSourceTypeSelect] = useState<
    ComboboxItem[]
  >([]);

  const [search, setSearch] = useState<any>({
    GroupBy: "dept",
    OverdueMinutes: "1440",
    FromDate: formatDateNotTimeZone(
      dayjs().subtract(14, "day").startOf("day").toDate(),
    ),
    ToDate: formatDateNotTimeZone(dayjs().endOf("day").toDate()),
    BranchId: "",
    SourceType: "",
    KeySearch: "",
  });

  const isGroupByEmp = search.GroupBy === "emp";

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "groupName",
        header: isGroupByEmp ? "Nhân viên" : "Phòng ban",
        size: 350,
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ renderedCellValue }) =>
          isGroupByEmp && renderedCellValue ? (
            <Flex align={"center"} gap={"md"}>
              {/* groupName dạng "mã - tên", avatar lấy theo tên */}
              <Avvvatars
                value={`${renderedCellValue
                  ?.toString()
                  ?.split(" - ")
                  .pop()
                  ?.normalize("NFD")}`}
              />
              <Text size="12.5px" fw={500}>
                {renderedCellValue}
              </Text>
            </Flex>
          ) : (
            <Text size="12.5px" fw={500}>
              {renderedCellValue}
            </Text>
          ),
      },
      {
        accessorKey: "totalOrders",
        header: "Số phiếu quá hạn",
        size: 180,
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        mantineTableHeadCellProps: { align: "right" },
        mantineTableBodyCellProps: { align: "right" },
        Cell: ({ renderedCellValue }) => (
          <Badge color="red" variant="light" size="lg" radius="sm">
            {renderedCellValue ?? 0}
          </Badge>
        ),
      },
      hoursColumn("totalOverdueHours", "Tổng số giờ quá hạn"),
      hoursColumn("avgOverdueHours", "Số giờ quá hạn trung bình"),
      hoursColumn("maxOverdueHours", "Số giờ quá hạn lâu nhất"),
    ],
    [isGroupByEmp],
  );

  const buildQuery = () => {
    const params = new URLSearchParams();
    Object.entries(search).forEach(([key, value]) => {
      if (typeof value === "string" && value.length > 0) {
        params.append(key, value);
      }
    });
    return params.toString();
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      const url = `/api/v1/RequestInventoryTransaction/get-outward-overdue-report?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}&${buildQuery()}`;

      const res = await repositoryPos.get<MessageResponse<any[]>>(url);

      if (res && res.success) {
        setData(res.data ?? []);
        setRowCount(res.totalCount);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const handleExportExcel = async () => {
    open();
    try {
      const _url = `${BASE_API_POS}/api/v1/RequestInventoryTransaction/export-outward-overdue-report?${buildQuery()}`;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        NotificationExtension.Success("Export Excel thành công !");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `bao_cao_phieu_can_xuat_kho_qua_han_${_name}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const getDataBranchSelect = async () => {
    const res = await getMasterOrgSelect();
    setDataBranchSelect(
      res
        ?.filter(
          (item) =>
            item.text != null && item.value != null && item.value !== "-1",
        )
        ?.map((item) => ({
          value: item.value?.toString(),
          label: `${item.att1} - ${item.text}`,
        })),
    );
  };

  const getDataSourceTypeSelect = async () => {
    const res = await getTblInventoryTransactionTypeOutWard();
    setDataSourceTypeSelect(
      res.map((type) => ({
        value: type.value.toString(),
        label: type.text,
      })),
    );
  };

  // Đổi kiểu gom nhóm thì cột bảng đổi theo, nên tải lại ngay từ trang đầu
  const changeGroupBy = (value: string | null) => {
    if (!value) return;
    changeSearchValue(value, "GroupBy", setSearch);
    setData([]);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    fetchData();
  }, [pagination]);

  useEffect(() => {
    getDataBranchSelect();
  }, []);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (140 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    enableTopToolbar: false,
    localization: _localization,
    enableRowSelection: false,
    initialState: {
      showColumnFilters: false,
      columnPinning: { left: ["mrt-row-numbers"] },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: () => ({
      style: {
        fontWeight: "500",
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
  });

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Button
            leftSection={<IconFileExport size={14} />}
            onClick={() => handleExportExcel()}
          >
            Export Excel
          </Button>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa"
              value={search.KeySearch}
              onChange={(e) =>
                changeSearchValue(e.currentTarget.value, "KeySearch", setSearch)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchData();
              }}
              leftSection={<IconSearch color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={search.FromDate ? new Date(search.FromDate) : null}
              maxDate={search.ToDate ? new Date(search.ToDate) : new Date()}
              onChange={(e) => {
                changeSearchValue(
                  e ? formatDateNotTimeZone(e) : "",
                  "FromDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              value={search.ToDate ? new Date(search.ToDate) : null}
              minDate={search.FromDate ? new Date(search.FromDate) : undefined}
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? formatDateNotTimeZone(dayjs(e).endOf("day").toDate())
                    : "",
                  "ToDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Tổng hợp theo"
              allowDeselect={false}
              leftSection={<IconUsersGroup color="#15aabf" />}
              comboboxProps={{
                transitionProps: { transition: "pop", duration: 200 },
              }}
              data={dataGroupByOption}
              value={search.GroupBy}
              onChange={changeGroupBy}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Ngưỡng quá hạn"
              searchable
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              allowDeselect={false}
              leftSection={<IconClockExclamation color="#15aabf" />}
              comboboxProps={{
                transitionProps: { transition: "pop", duration: 200 },
              }}
              data={dataOverdueMinutesOption}
              value={search.OverdueMinutes}
              onChange={(e) =>
                changeSearchValue(e ? e : "1440", "OverdueMinutes", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Nhập mã hoặc tên chi nhánh"
              searchable
              clearable
              leftSection={<IconBrandBootstrap color="#15aabf" />}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              data={dataBranchSelect}
              value={search.BranchId || null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "BranchId", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Loại chứng từ gốc"
              searchable
              clearable
              leftSection={<IconFileTypography color="#15aabf" />}
              comboboxProps={{
                transitionProps: { transition: "pop", duration: 200 },
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
              data={dataSourceTypeSelect}
              value={search.SourceType || null}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "SourceType", setSearch)
              }
              onClick={() => {
                if (dataSourceTypeSelect.length === 0) {
                  getDataSourceTypeSelect();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default OverduePendingOutboundReport;
